import { Component, OnInit, Inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AppVersionService } from './services/app-version.service';
import { HolidayNotificationService } from './services/holiday-notification.service';
import { CommandPaletteService } from './services/command-palette.service';
import { PermissionsService } from './services/permissions.service';
import { IncidentBannerService } from './services/incident-banner.service';
import { SupportRequestComponent } from './components/forms/support-request/support-request.component';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from './services/language.service';
import { WhatsNewService } from './services/whats-new.service';
import { environment } from '../environments/environment';

/**
 * AppComponent - Root component
 *
 * Conditionally shows/hides menu, footer, and notifications based on route:
 * - Regular routes (/dashboard, /students, etc): Show full chrome (menu, footer)
 * - Widget routes (/widgets/*): Hide chrome for clean embedding
 *
 * This allows widgets to be embedded in iframes without duplicate navigation.
 * Widget routes use WidgetModule with WidgetLayoutComponent for minimal layout.
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
  supportRequestForm = SupportRequestComponent;

  /** True when current route starts with /widgets - used to hide menu/footer */
  isWidgetRoute = false;

  constructor(
    public versionService: AppVersionService,
    public holidayService: HolidayNotificationService,
    public permissions: PermissionsService,
    public incidentBanner: IncidentBannerService,
    private commandPaletteService: CommandPaletteService,
    private translate: TranslateService,
    private languageService: LanguageService,
    private whatsNewService: WhatsNewService,
    private router: Router
  ) {
    // Make command palette service globally accessible
    (window as any).commandPaletteService = commandPaletteService;
    // Apply the cached language immediately so there's no flash of the default
    // before login resolves; the user's saved preferred_langcode is applied once
    // current_user loads (NgInitDataService.init).
    translate.setDefaultLang('en');
    this.languageService.applyLanguage(this.languageService.getCachedLanguage());

    // Check if current route is a widget route
    this.checkIfWidgetRoute(this.router.url);

    // Listen for route changes
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.checkIfWidgetRoute(event.urlAfterRedirects);
      // Refresh banner state on route changes so login/logout pick up latest.
      this.incidentBanner.refresh();
      // Once per sign-in, open What's New if there's a month this user hasn't seen.
      this.whatsNewService.maybeShowLatest(event.urlAfterRedirects);
    });
  }

  ngOnInit() {
    this.incidentBanner.startPolling();

    // Load Arthur Murray widget only in production
    if (environment.production) {
      this.loadArthurMurrayWidget();
    }
  }

  private checkIfWidgetRoute(url: string): void {
    this.isWidgetRoute = url.startsWith('/widgets');
  }


  private loadArthurMurrayWidget() {
    // Check if script is already loaded
    if (document.getElementById('contactus-jssdk')) {
      return;
    }

    const script = document.createElement('script');
    script.id = 'contactus-jssdk';
    script.src = 'https://api.arthurmurray.online/widget/e91a9f0b-4253-311f-8ba1-0f4cbc14d867?r=' + encodeURIComponent(window.location.href);
    script.async = true;

    // Insert script into head
    document.head.appendChild(script);
  }
}
